// SearchMessageRow — one message result from fn_work_search (work-page-fixes §C).
//
// fn_work_search returns { work: [], messages: [] }. The work page renders the
// work items but never the messages. This row is the per-message card shown in a
// "Messages" section below the work items. When the message carries a task_id
// (added to fn_work_search per Brian's fix), the whole row is clickable and
// opens the associated task's slide-out via onSelectTask. Messages without a
// task_id render as a non-clickable, informational card.
//
// Message fields (from the actual API response): id, from (sender), channel
// (e.g. "email"), preview (body snippet), at (timestamp), task_id, task_title.
// Any field may be missing; the row degrades gracefully and never throws on a
// partial object.
import React from "react";
import { formatWhen } from "../../../shared/format";

// Relative time — matches the local helper repeated across the dashboard
// (CdpInsightsTab, PersonProfile, LiveFeed, …): "12m ago" / "3h ago" / "5d ago".
function relativeTime(ts) {
  if (!ts) return "";
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (Number.isNaN(s)) return "";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Truncate a preview string to ~max chars with an ellipsis. Strips leading/
// trailing whitespace so a multi-line email body reads as a single-line snippet.
function truncatePreview(value, max = 120) {
  if (!value) return "";
  const one = String(value).replace(/\s+/g, " ").trim();
  if (one.length <= max) return one;
  return `${one.slice(0, max).trimEnd()}…`;
}

/**
 * @param {object} props
 * @param {object} props.message  One entry from fn_work_search `messages`.
 * @param {(id: string) => void} [props.onSelectTask]  Open a task's slide-out
 *   by id. When the message carries a `task_id`, the whole row is clickable and
 *   calls this with that id; rows without a `task_id` stay non-clickable.
 */
export default function SearchMessageRow({ message, onSelectTask }) {
  const m = message || {};
  const from = m.from || "Unknown sender";
  const channel = m.channel || null;
  const preview = truncatePreview(m.preview);
  const when = relativeTime(m.at);
  const absWhen = formatWhen(m.at); // absolute date, shown as a tooltip
  // task_id / task_title were added to fn_work_search message results so a
  // message can be linked back to the task it belongs to. When task_id is
  // present (and a callback is wired), the row opens that task's slide-out.
  const taskId = m.task_id || null;
  const taskTitle = m.task_title || null;
  const clickable = !!taskId && typeof onSelectTask === "function";

  return (
    <div
      data-testid="search-message-row"
      data-message-id={m.id || undefined}
      data-task-id={taskId || undefined}
      onClick={clickable ? () => onSelectTask(taskId) : undefined}
      className={`rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800/50${
        clickable
          ? " cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded"
          : ""
      }`}
    >
      {/* Sender (primary, left-aligned) · channel badge · timestamp */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {from}
        </span>
        {channel && (
          <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            {channel}
          </span>
        )}
        {when && (
          <span
            className="ml-auto shrink-0 text-xs text-gray-400 tabular-nums dark:text-gray-500"
            title={absWhen || undefined}
          >
            {when}
          </span>
        )}
      </div>

      {/* Preview — the body snippet, truncated to ~120 chars */}
      {preview && (
        <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-400">
          {preview}
        </p>
      )}

      {/* Task association — which task the message belongs to. Only rendered
          when the message has a task_title (and therefore a task_id). */}
      {taskTitle && (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
          in: {taskTitle}
        </p>
      )}
    </div>
  );
}
